import { Children, Fragment, forwardRef, isValidElement, useState, type ReactNode } from "react";
import { cn } from "~/lib/cn";

interface SelectProps extends React.ComponentProps<"select"> {
  label?: string;
  labelAside?: ReactNode;
  error?: string;
  /** Extra class applied to the .af-label element. */
  labelClassName?: string;
  /**
   * `public` (T872 S5) — the October delivery's `.test-group`: a sentence-case 14px sans
   * label over a bordered native select (the browser's own chevron), the error line
   * under it. `default` is the auth-form select (`.af-*`).
   */
  variant?: "default" | "public";
}

/**
 * T596 — is the option `value` resolves to a placeholder? The delivered select marks its
 * placeholder `<option value="" disabled hidden>` and mutes the closed control while that
 * is what shows (`.af-select.is-placeholder`). "Disabled" is the test, not "empty": an
 * empty value that is a real choice (`/tu-opinion`'s "general", `/tu-historia`'s "none")
 * is an answer she gave and reads at full weight. Walks the children so the server and
 * the first client paint agree without touching the DOM.
 */
function isPlaceholderSelected(value: unknown, children: ReactNode): boolean {
  if (value == null) return false;
  const target = String(value);
  let placeholder = false;
  const visit = (nodes: ReactNode) =>
    Children.forEach(nodes, (child) => {
      if (!isValidElement<{ children?: ReactNode }>(child)) return;
      if (child.type === Fragment) return visit(child.props.children);
      if (child.type !== "option") return;
      const { value: optionValue, children: text, disabled } = child.props as React.ComponentProps<"option">;
      const resolved = optionValue ?? (typeof text === "string" ? text : "");
      if (String(resolved) === target && disabled) placeholder = true;
    });
  visit(children);
  return placeholder;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    { label, labelAside, error, labelClassName, className, id, disabled, children, onChange, variant = "default", "aria-describedby": describedBy, ...props },
    ref,
  ) {
    const selectId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    const helpId = error ? `${selectId}-error` : undefined;
    // A consumer's own description (a hint under the field) is kept beside the error's id.
    const describedByIds = [describedBy, helpId].filter(Boolean).join(" ") || undefined;

    if (variant === "public") {
      return (
        <div className={cn("pub-field", error && "is-error")}>
          {label && (
            <label htmlFor={selectId} className={cn("pub-field-label", labelClassName)}>
              {label}
            </label>
          )}
          {/* The chevron is the wrapper's `::after` (production review 2026-10-09): the native
              arrow ignores padding, so a long option ran under it and the arrow touched the
              border. The native picker, keyboard and focus ring are untouched. */}
          <span className="pub-field-select-wrap">
            <select
              ref={ref}
              id={selectId}
              className={cn("pub-field-select", className)}
              aria-invalid={error ? true : undefined}
              aria-describedby={describedByIds}
              disabled={disabled}
              onChange={onChange}
              {...props}
            >
              {children}
            </select>
          </span>
          {error && (
            <p id={helpId} className="pub-field-error" role="alert">
              {error}
            </p>
          )}
        </div>
      );
    }

    // A disabled option cannot be picked, so once an uncontrolled select changes it never
    // shows its placeholder again; controlled selects answer from `value` on every render.
    const [chosen, setChosen] = useState(false);
    const showPlaceholder =
      props.value !== undefined
        ? isPlaceholderSelected(props.value, children)
        : !chosen && isPlaceholderSelected(props.defaultValue, children);

    return (
      <div className="af-field">
        {label && !labelAside && (
          <label htmlFor={selectId} className={cn("af-label", labelClassName)}>
            <span>{label}</span>
          </label>
        )}
        {label && labelAside && (
          <div className="flex items-start justify-between gap-2">
            <label htmlFor={selectId} className={cn("af-label", labelClassName)}>
              <span>{label}</span>
            </label>
            {labelAside}
          </div>
        )}
        <div
          className={cn(
            "af-select-wrap",
            error && "is-error",
            disabled && "is-disabled",
          )}
        >
          <select
            ref={ref}
            id={selectId}
            className={cn("af-select", showPlaceholder && "is-placeholder", className)}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedByIds}
            disabled={disabled}
            onChange={(e) => {
              setChosen(true);
              onChange?.(e);
            }}
            {...props}
          >
            {children}
          </select>
          <svg
            className="af-select-chevron"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="2 4 6 8 10 4" />
          </svg>
        </div>
        {error && (
          <p id={helpId} className="af-helper is-error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);
